import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';
import { scrollState, smoothstep, clamp } from '@/lib/depth';
import { HeroContent } from '@/components/sections/HeroContent';
import { StoryCard } from '@/components/sections/StoryCard';
import { WorkCard } from '@/components/sections/WorkCard';
import { SocialCard } from '@/components/sections/SocialCard';
import { LinksCard } from '@/components/sections/LinksCard';
import { ThresholdLine } from '@/components/sections/ThresholdLine';
import { KingBelowCard } from '@/components/sections/KingBelowCard';
import { LettersCard } from '@/components/sections/LettersCard';
import { EndNote } from '@/components/sections/EndNote';

// Mobile intro choreography, in DioramaZoom camera units. The overlays read the
// same camera space as the hero images so they compose in exact sync. On mobile
// the camera advances faster than raw scroll (GAIN) so the whole intro — hero1
// zooming away, the About, hero2 sweeping off — happens in far less scrolling.
const GAIN = 2.3;
const ABOUT_IN = 2150;      // hero2 (cliff) is firmly in place — layer the About in
const H2_OUT_START = 3200;  // hero2 begins its grow + sweep-off-left (== DioramaZoom)
const H2_OUT_END = 4200;    // hero2 fully gone — clean hero3 (clouds) remains
const RUNWAY_PX = Math.round(H2_OUT_END / GAIN); // scroll px the intro plays over

const MobileSection = ({ id, Comp, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.18 });
  return (
    <section id={`m-${id}`} ref={ref} className={`flex justify-center px-4 py-12 ${className}`}>
      <Comp active={inView} />
    </section>
  );
};

/**
 * Below the md breakpoint the 3D z-axis mechanic is abandoned for a clean
 * vertical scroll. The cinematic intro is preserved as a scroll-driven sequence:
 *
 *   1. The home text rests over hero1 (the diorama) and fades as hero1 zooms away.
 *   2. Once hero1 is gone and hero2 (the cliff) is firmly in place, the About
 *      text layers in over it — nothing else is on screen yet.
 *   3. The About dissolves out exactly as hero2 grows + sweeps off-frame left.
 *   4. With only hero3 (the clouds) left, the remaining sections scroll as text.
 */
export default function MobileExperience() {
  const heroRef = useRef(null); // fixed home text overlay
  const aboutRef = useRef(null); // fixed About overlay (handles the exit fade)
  const aboutLatched = useRef(false); // latch guard — kept in a ref so the
  // scroll-driver effect never re-runs (re-running would window.scrollTo(0,0)
  // and snap the viewer back to the top).
  const [aboutActive, setAboutActive] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    scrollState.camera = 0;
    let raf;
    const tick = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      const cam = y * GAIN;
      scrollState.camera = cam;

      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const f = max > 0 ? y / max : 0;
      scrollState.progress = f;
      scrollState.blend = smoothstep(0.56, 0.74, f);

      // Home text: full at the top, faded out before hero1 has fully zoomed away
      // so the last of the zoom reveals a clean hero2.
      if (heroRef.current) {
        const o = clamp(1 - (cam - 500) / 1000, 0, 1);
        heroRef.current.style.opacity = o.toFixed(3);
        heroRef.current.style.visibility = o < 0.01 ? 'hidden' : 'visible';
      }

      // About: layers in once hero2 is settled (latched, like the home text),
      // then dissolves on exit in lock-step with hero2's sweep-off.
      const exit = clamp(1 - (cam - H2_OUT_START) / (H2_OUT_END - H2_OUT_START), 0, 1);
      const shown = cam > ABOUT_IN;
      const aop = shown ? exit : 0;
      if (aboutRef.current) {
        aboutRef.current.style.opacity = aop.toFixed(3);
        aboutRef.current.style.visibility = aop < 0.01 ? 'hidden' : 'visible';
      }
      if (shown && !aboutLatched.current) {
        aboutLatched.current = true;
        setAboutActive(true);
      }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <main className="relative z-10" data-testid="mobile-experience" style={{ touchAction: 'pan-y' }}>
      {/* Home text — fixed over the zooming hero1, fades as it departs */}
      <div
        ref={heroRef}
        className="fixed inset-0 z-[6] pointer-events-none"
        style={{ willChange: 'opacity' }}
        data-testid="m-hero-overlay"
      >
        <HeroContent />
      </div>

      {/* About — fixed, composes over the settled hero2 and exits with it */}
      <div
        ref={aboutRef}
        className="fixed inset-0 z-[8] flex items-center justify-center px-5 pointer-events-none"
        style={{ opacity: 0, visibility: 'hidden', willChange: 'opacity', transition: 'opacity 0.5s ease' }}
        aria-hidden="true"
        data-testid="m-about-overlay"
      >
        <StoryCard active={aboutActive} />
      </div>

      {/* Intro runway — the scroll distance the hero choreography plays out over.
          No text sections appear until hero2 is fully gone. */}
      <div id="m-story" aria-hidden="true" style={{ height: `calc(${RUNWAY_PX}px + 100vh)` }} />

      {/* With only hero3 (clouds) behind, the rest scrolls as plain text. */}
      <MobileSection id="work" Comp={WorkCard} />
      <MobileSection id="social" Comp={SocialCard} />
      <MobileSection id="links" Comp={LinksCard} />
      <div className="flex justify-center py-20">
        <ThresholdLine />
      </div>
      <MobileSection id="tkb" Comp={KingBelowCard} />
      <MobileSection id="letters" Comp={LettersCard} />
      <MobileSection id="end" Comp={EndNote} className="pt-12 pb-40" />
    </main>
  );
}
